import React, { useState, useCallback, useMemo } from 'react';
import { Radio, Select, Input, Button, Table, Alert } from 'antd';
import { apiHolder, apis } from 'api';
const TABLE_FIELDS = {
    contact: [
        'id',
        'contactName',
        'contactPYName',
        'accountName',
        'displayEmail',
        'type',
        'enterpriseId',
        'accountStatus',
        'visibleCode',
        'marked',
        '_lastUpdateTime',
        'accountOriginId',
        'source',
        'contactLabel',
    ],
    contactItem: ['id', 'contactId', 'contactItemVal', 'contactItemType', 'type', 'enterpriseId', '_lastUpdateTime', 'emailType', 'isDefault'],
    org: ['id', 'originId', 'orgName', 'orgPYName', 'parent', 'type', 'enterpriseId', 'level', 'visibleCode', '_lastUpdateTime', 'source'],
    orgContact: ['id', 'contactId', 'orgId', 'yunxinId', 'type', 'enterpriseId', '_lastUpdateTime', 'imId'],
    personal_mark: ['id', 'type', 'value', 'name', 'marked', '_lastUpdateTime'],
    frequentContact: ['id', 'contactId', 'email', 'timestamp', 'sendcount', 'type', 'channel'],
    recent_contact: ['email', 'accountId', 'index', 'nickname'],
    lx_info: ['contactId', 'avatar', 'yxid', 'yxToken'],
};
const TABLE_NAMES = Object.keys(TABLE_FIELDS);
const OPERATORS = [
    { value: 'equals', label: '等于 (=)' },
    { value: 'contains', label: '包含 (模糊)' },
    { value: 'equalsIgnoreCase', label: '等于 (忽略大小写)' },
    { value: 'startsWith', label: '前缀匹配' },
    { value: 'startsWithIgnoreCase', label: '前缀匹配 (忽略大小写)' },
    { value: 'above', label: '大于 (>)' },
    { value: 'below', label: '小于 (<)' },
    { value: 'aboveOrEqual', label: '大于等于 (>=)' },
    { value: 'belowOrEqual', label: '小于等于 (<=)' },
    { value: 'anyOf', label: '枚举 (IN)' },
    { value: 'containsAny', label: '数组包含' },
];
const PAGE_SIZE = 50;
const NUMERIC_OPERATORS = new Set(['above', 'below', 'aboveOrEqual', 'belowOrEqual']);
const FILTER_BASED_OPERATORS = new Set(['contains', 'equalsIgnoreCase', 'startsWithIgnoreCase']);
function getDbApi(dataSource) {
    const apiName = dataSource === 'dexie' ? apis.dexieDbApi : apis.dbMemoryApiImpl;
    return apiHolder.api.requireLogicalApi(apiName);
}
function getDbName(dataSource) {
    return dataSource === 'dexie' ? 'contact_dexie' : 'contact_search';
}
function parseSearchValue(value, operator) {
    if (operator === 'anyOf') {
        return value.split(',').map(v => v.trim());
    }
    if (NUMERIC_OPERATORS.has(operator)) {
        const num = Number(value);
        if (!Number.isNaN(num))
            return num;
    }
    return value;
}
function resolveAccountMd5(account) {
    var _a;
    if (account.accountMd5)
        return account.accountMd5;
    const systemApi = apiHolder.api.getSystemApi();
    const user = systemApi.getCurrentUser(account.id);
    return (_a = user === null || user === void 0 ? void 0 : user.accountMd5) !== null && _a !== void 0 ? _a : '';
}
function clientFilter(items, field, operator, value) {
    const lowerValue = value.toLowerCase();
    return items.filter(item => {
        const raw = item[field];
        const str = raw === null || raw === undefined ? '' : String(raw);
        switch (operator) {
            case 'contains':
                return str.toLowerCase().includes(lowerValue);
            case 'equalsIgnoreCase':
                return str.toLowerCase() === lowerValue;
            case 'startsWithIgnoreCase':
                return str.toLowerCase().startsWith(lowerValue);
            case 'equals':
                return str === value;
            case 'startsWith':
                return str.startsWith(value);
            default:
                return true;
        }
    });
}
const DataSearchSection = ({ accounts }) => {
    var _a, _b;
    const [dataSource, setDataSource] = useState('dexie');
    const [selectedAccount, setSelectedAccount] = useState((_b = (_a = accounts[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '');
    const [selectedTable, setSelectedTable] = useState('');
    const [selectedField, setSelectedField] = useState('');
    const [selectedOperator, setSelectedOperator] = useState('equals');
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const hasCondition = Boolean(selectedField && searchValue);
    const fieldOptions = useMemo(() => {
        if (!selectedTable || !TABLE_FIELDS[selectedTable])
            return [];
        return TABLE_FIELDS[selectedTable].map(f => ({ value: f, label: f }));
    }, [selectedTable]);
    const columns = useMemo(() => {
        if (!selectedTable || !TABLE_FIELDS[selectedTable])
            return [];
        return TABLE_FIELDS[selectedTable].map(fieldName => ({
            title: fieldName,
            dataIndex: fieldName,
            key: fieldName,
            ellipsis: true,
            width: fieldName === 'id' ? 200 : 150,
            render: (val) => {
                if (val === null || val === undefined)
                    return '—';
                if (typeof val === 'object')
                    return JSON.stringify(val);
                return String(val);
            },
        }));
    }, [selectedTable]);
    const handleTableChange = useCallback((tableName) => {
        setSelectedTable(tableName);
        setSelectedField('');
        setResults([]);
        setTotalCount(0);
        setCurrentPage(1);
        setHasSearched(false);
        setSearchError(null);
    }, []);
    const clearResults = useCallback(() => {
        setResults([]);
        setTotalCount(0);
        setCurrentPage(1);
    }, []);
    const doSearch = useCallback(async (page) => {
        if (!selectedTable)
            return;
        const account = accounts.find(a => a.id === selectedAccount);
        if (!account)
            return;
        setSearchLoading(true);
        setSearchError(null);
        try {
            const dbApi = getDbApi(dataSource);
            const dbName = getDbName(dataSource);
            const accountMd5 = resolveAccountMd5(account);
            const start = (page - 1) * PAGE_SIZE;
            let data;
            let total;
            const useFilterBased = hasCondition && FILTER_BASED_OPERATORS.has(selectedOperator);
            if (!hasCondition || useFilterBased) {
                const query = {
                    dbName,
                    tableName: selectedTable,
                    _dbAccount: accountMd5,
                };
                if (useFilterBased) {
                    const allData = (await dbApi.getByEqCondition(query, accountMd5));
                    const filtered = clientFilter(allData, selectedField, selectedOperator, searchValue);
                    total = filtered.length;
                    data = filtered.slice(start, start + PAGE_SIZE);
                }
                else {
                    query.count = PAGE_SIZE;
                    query.start = start;
                    const dbApiExt = dbApi;
                    if (typeof dbApiExt.getByEqConditionWithTotal === 'function') {
                        const res = await dbApiExt.getByEqConditionWithTotal(query, accountMd5);
                        data = Array.isArray(res.data) ? res.data : [];
                        total = typeof res.totalCount === 'number' ? res.totalCount : undefined;
                    }
                    else {
                        data = (await dbApi.getByEqCondition(query, accountMd5));
                    }
                }
            }
            else if (selectedOperator === 'equals') {
                const parsedValue = parseSearchValue(searchValue, selectedOperator);
                const query = {
                    dbName,
                    tableName: selectedTable,
                    query: { [selectedField]: parsedValue },
                    count: PAGE_SIZE,
                    start,
                    _dbAccount: accountMd5,
                };
                try {
                    const dbApiExt = dbApi;
                    if (typeof dbApiExt.getByEqConditionWithTotal === 'function') {
                        const res = await dbApiExt.getByEqConditionWithTotal(query, accountMd5);
                        data = Array.isArray(res.data) ? res.data : [];
                        total = typeof res.totalCount === 'number' ? res.totalCount : undefined;
                    }
                    else {
                        data = (await dbApi.getByEqCondition(query, accountMd5));
                    }
                }
                catch (_a) {
                    const fallbackQuery = { dbName, tableName: selectedTable, _dbAccount: accountMd5 };
                    const allData = (await dbApi.getByEqCondition(fallbackQuery, accountMd5));
                    const filtered = clientFilter(allData, selectedField, 'equals', searchValue);
                    total = filtered.length;
                    data = filtered.slice(start, start + PAGE_SIZE);
                }
            }
            else {
                const parsedValue = parseSearchValue(searchValue, selectedOperator);
                const args = selectedOperator === 'anyOf' ? [parsedValue] : [parsedValue];
                const query = {
                    dbName,
                    tableName: selectedTable,
                    adCondition: {
                        type: selectedOperator,
                        field: selectedField,
                        args,
                    },
                    count: PAGE_SIZE,
                    start,
                    _dbAccount: accountMd5,
                };
                try {
                    const dbApiExt = dbApi;
                    if (typeof dbApiExt.getByRangeConditionWithTotalCount === 'function') {
                        const res = await dbApiExt.getByRangeConditionWithTotalCount(query, accountMd5);
                        data = Array.isArray(res.data) ? res.data : [];
                        total = typeof res.totalCount === 'number' ? res.totalCount : undefined;
                    }
                    else {
                        data = (await dbApi.getByRangeCondition(query, accountMd5));
                    }
                }
                catch (_b) {
                    const fallbackQuery = { dbName, tableName: selectedTable, _dbAccount: accountMd5 };
                    const allData = (await dbApi.getByEqCondition(fallbackQuery, accountMd5));
                    const filtered = clientFilter(allData, selectedField, selectedOperator, searchValue);
                    total = filtered.length;
                    data = filtered.slice(start, start + PAGE_SIZE);
                }
            }
            const resultArray = Array.isArray(data) ? data : [];
            setResults(resultArray);
            setCurrentPage(page);
            if (total !== undefined) {
                setTotalCount(total);
            }
            else if (page === 1) {
                const tableTotal = await dbApi.getTableCount({ dbName, tableName: selectedTable }, accountMd5).catch(() => resultArray.length);
                setTotalCount(hasCondition ? resultArray.length : tableTotal);
            }
            setHasSearched(true);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setSearchError(message);
            clearResults();
            setHasSearched(true);
        }
        finally {
            setSearchLoading(false);
        }
    }, [selectedTable, selectedField, searchValue, selectedOperator, selectedAccount, accounts, dataSource, hasCondition, clearResults]);
    const handleSearch = useCallback(() => {
        setCurrentPage(1);
        doSearch(1);
    }, [doSearch]);
    const handlePageChange = useCallback((page) => {
        doSearch(page);
    }, [doSearch]);
    const tableLocale = useMemo(() => {
        if (!hasSearched)
            return { emptyText: '选择表后点击搜索查看数据' };
        return { emptyText: '无匹配数据' };
    }, [hasSearched]);
    return (React.createElement("div", null,
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 } },
            React.createElement(Radio.Group, { value: dataSource, onChange: e => setDataSource(e.target.value), size: "small" },
                React.createElement(Radio.Button, { value: "dexie" }, "Dexie"),
                React.createElement(Radio.Button, { value: "loki" }, "Loki")),
            React.createElement(Select, { value: selectedAccount, onChange: setSelectedAccount, style: { width: 200 }, size: "small", placeholder: "\u9009\u62E9\u8D26\u53F7" }, accounts.map(acc => (React.createElement(Select.Option, { key: acc.id, value: acc.id }, acc.id)))),
            React.createElement(Select, { value: selectedTable || undefined, onChange: handleTableChange, style: { width: 180 }, size: "small", placeholder: "\u9009\u62E9\u8868" }, TABLE_NAMES.map(name => (React.createElement(Select.Option, { key: name, value: name }, name))))),
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
            React.createElement(Select, { value: selectedField || undefined, onChange: setSelectedField, style: { width: 180 }, size: "small", placeholder: "\u5B57\u6BB5 (\u53EF\u4E0D\u9009)", disabled: !selectedTable, options: fieldOptions, allowClear: true }),
            React.createElement(Select, { value: selectedOperator, onChange: setSelectedOperator, style: { width: 180 }, size: "small", disabled: !selectedField }, OPERATORS.map(op => (React.createElement(Select.Option, { key: op.value, value: op.value }, op.label)))),
            React.createElement(Input, { value: searchValue, onChange: e => setSearchValue(e.target.value), placeholder: !selectedField ? '不选字段则查全表' : selectedOperator === 'anyOf' ? '逗号分隔多个值' : '输入查询值', style: { width: 200 }, size: "small", onPressEnter: handleSearch, disabled: !selectedField }),
            React.createElement(Button, { type: "primary", size: "small", onClick: handleSearch, loading: searchLoading, disabled: !selectedTable }, "\u641C\u7D22")),
        searchError && React.createElement(Alert, { type: "error", message: searchError, style: { marginBottom: 8 }, closable: true, onClose: () => setSearchError(null) }),
        React.createElement(Table, { columns: columns, dataSource: results, rowKey: (_, index) => String(index), size: "small", loading: searchLoading, locale: tableLocale, scroll: { x: 'max-content' }, pagination: {
                pageSize: PAGE_SIZE,
                current: currentPage,
                total: totalCount,
                showTotal: (t) => `共 ${t} 条`,
                onChange: handlePageChange,
            } })));
};
export default DataSearchSection;
